/* Pill button primitive for the warm-editorial design language.
 *
 * Variants are fully self-contained class strings (border width + color
 * live inside each variant, never split base/conditional) so Tailwind's
 * JIT ordering can't produce a nondeterministic winner. Filled variants
 * carry a transparent border so all variants render the same box size.
 *
 * `as` swaps the rendered element — pass `as={Link}` + `to` for router
 * CTAs. When rendering a native <button> the type defaults to "button"
 * so a Button inside a <form> doesn't submit by accident; pass
 * type="submit" explicitly where submission is the intent.
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-display font-semibold tracking-tight transition ' +
  // Opaque ring + offset gap — a stronger variant of the .focus-ring utility
  // in index.css, upgraded to focus-visible (keyboard-only) and primary-600
  // (the utility uses focus: + orange-500, i.e. primary-500). The ring must be
  // opaque: translucent rings fail the 3:1 non-text contrast minimum on cream
  // and ink surfaces. primary-600 clears 3:1 on cream-50 (3.50:1), ink (4.69:1)
  // and the white offset gap (3.56:1); the offset keeps it visible on the
  // brand-orange fill.
  'motion-safe:active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 ' +
  'disabled:opacity-50 disabled:pointer-events-none select-none';

const VARIANTS = {
  ink: 'border-[1.5px] border-transparent bg-ink text-cream-50 hover:bg-ink-700',
  brand: 'border-[1.5px] border-transparent bg-primary-500 text-white hover:bg-primary-600',
  ghost: 'border-[1.5px] border-ink/20 bg-transparent text-ink hover:bg-ink/5',
  // For dark (ink) surfaces, where ink/ghost would vanish.
  cream: 'border-[1.5px] border-transparent bg-cream-50 text-ink hover:bg-white',
  // Outline-on-dark: transparent fill, cream border — the secondary
  // action on an ink band, where the ink-bordered `ghost` is invisible.
  ghostCream: 'border-[1.5px] border-cream-50/30 bg-transparent text-cream-50 hover:bg-cream-50/10 focus-visible:ring-offset-ink',
};

const SIZES = {
  sm: 'px-4 py-2 text-[13px]',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3 text-base',
};

export default function Button({
  as: Tag = 'button',
  variant = 'ink',
  size = 'md',
  className = '',
  children,
  ...props
}) {
  const variantClass = typeof VARIANTS[variant] === 'string' ? VARIANTS[variant] : VARIANTS.ink;
  const sizeClass = typeof SIZES[size] === 'string' ? SIZES[size] : SIZES.md;
  return (
    <Tag
      type={Tag === 'button' ? 'button' : undefined}
      className={`${BASE} ${variantClass} ${sizeClass} ${className}`}
      {...props}
    >
      {children}
    </Tag>
  );
}
